/**
 * Pricing helpers shared by the API and the Admin frontend.
 */

const toFiniteNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Compute the marked-up price per litre that Ministry of Wine charges its
 * customers: the supplier's price per litre (as stored in the database) plus
 * the margin.
 *
 * - A `'%'` margin unit is applied as a percentage: `price * (1 + margin / 100)`.
 * - A `'$'` margin unit is applied as a fixed amount: `price + margin`.
 *
 * Returns `null` when there is no price per litre to mark up. When the margin is
 * missing/invalid, or the margin unit is unrecognised/missing, the unmarked
 * price per litre is returned unchanged — we never apply a markup we can't
 * attribute to a known unit, to avoid silently overcharging.
 */
export const calcPricePerLitreWithMargin = (
  pricePerLitre: number | string | null | undefined,
  margin: number | string | null | undefined,
  marginUnit: string | null | undefined,
): number | null => {
  const price = toFiniteNumber(pricePerLitre);
  if (price === null) {
    return null;
  }

  const marginValue = toFiniteNumber(margin);
  if (marginValue === null) {
    return price;
  }

  if (marginUnit === '%') {
    return price * (1 + marginValue / 100);
  }

  if (marginUnit === '$') {
    return price + marginValue;
  }

  return price;
};

/**
 * Format a monetary amount to a fixed number of decimal places using
 * round-half-up on a minor-unit (cents) basis.
 *
 * This exists so the PDF and the email render an identical marked-up price.
 * `Number(v).toFixed(2)` rounds the raw binary double, so a value like 1.005 —
 * stored as 1.00499999999999989 — formats to "1.00", while a decimal round-half-up
 * of 1.005 yields "1.01". Routing every customer-facing price through this helper
 * guarantees the two documents never disagree by a cent.
 *
 * We nudge the scaled value by a single, sign-aware unit of relative precision
 * (`* (1 + Number.EPSILON)`) before rounding. That is just enough to pull a value
 * whose exact decimal ends in "…5" back across the half-cent boundary that binary
 * representation error would otherwise drag it below, without affecting any value
 * that is not already sitting on that boundary.
 *
 * Returns `null` when the value is missing/non-finite.
 */
export const formatMoneyAmount = (
  value: number | string | null | undefined,
  decimals = 2,
): string | null => {
  const num = toFiniteNumber(value);
  if (num === null) {
    return null;
  }

  const factor = 10 ** decimals;
  const scaled = num * factor;
  return (Math.round(scaled * (1 + Number.EPSILON)) / factor).toFixed(decimals);
};
